import React, { useState } from "react";
import { AppButton } from "components/shared/AppButton";
import { ConfirmDialog } from "components/shared/ConfirmDialog";
import { useAuth } from "context/AuthContext";
import { useDeleteEcoSaltUpgrade, useEcoSaltUpgradeList } from "hooks/useEcoSaltUpgrade";
import { notify } from "utils/notify";
import { Card, CardTitle, EmptyState, Notice, UpgradeTable } from "styles/ecosaltUpgrade.styles";

const when = (iso: string) => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
};

/** Admin-only list of Nippers already upgraded, original size to new size. */
function EcoSaltUpgradesPanel({ onDeleted }: { onDeleted?: (serial: string) => void }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMIN";
  const query = useEcoSaltUpgradeList(isAdmin);
  const remove = useDeleteEcoSaltUpgrade();
  const [pendingSerial, setPendingSerial] = useState<string | null>(null);
  if (!isAdmin) return null;

  const rows = query.data?.upgrades ?? [];

  async function confirmDelete() {
    if (!pendingSerial) return;
    try {
      await remove.mutateAsync(pendingSerial);
      notify.success(`Deleted ${pendingSerial}`);
      onDeleted?.(pendingSerial);
      setPendingSerial(null);
    } catch {
      notify.error("Could not delete this record");
    }
  }

  return (
    <Card aria-labelledby="ecosalt-upgrades-title">
      <CardTitle id="ecosalt-upgrades-title">Upgraded Nippers</CardTitle>
      {query.isError && <Notice $tone="error">Could not load the upgrade list.</Notice>}
      {query.isLoading && <EmptyState>Loading upgrades...</EmptyState>}
      {!query.isLoading && !query.isError && rows.length === 0 && (
        <EmptyState>No Nippers have been upgraded yet.</EmptyState>
      )}
      {rows.length > 0 && (
        <UpgradeTable>
          <thead>
            <tr>
              <th>Serial</th>
              <th>From</th>
              <th>To</th>
              <th>When</th>
              <th>By</th>
              <th> </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.nipperSerial}>
                <td>{row.nipperSerial}</td>
                <td>{row.fromCellSize}g</td>
                <td>{row.toCellSize}g</td>
                <td>{when(row.upgradedAt)}</td>
                <td>{row.dealerEmail || row.dealerName || "—"}</td>
                <td>
                  <AppButton
                    variant="ghost"
                    aria-label={`Delete ${row.nipperSerial}`}
                    onClick={() => setPendingSerial(row.nipperSerial)}
                  >
                    Delete
                  </AppButton>
                </td>
              </tr>
            ))}
          </tbody>
        </UpgradeTable>
      )}
      <ConfirmDialog
        visible={pendingSerial !== null}
        title="Delete EcoSalt record"
        message={pendingSerial ? `Delete the EcoSalt record for ${pendingSerial}? The Nipper itself is not changed.` : ""}
        onHide={() => !remove.isPending && setPendingSerial(null)}
        onConfirm={() => void confirmDelete()}
        isProcessing={remove.isPending}
        confirmButtonLabel="Delete"
        cancelButtonLabel="Cancel"
      />
    </Card>
  );
}

export default EcoSaltUpgradesPanel;
